import React, { useState } from 'react'
import { Table, Form, Row, Col, Input, Button, DatePicker, Select } from 'antd'
import { MinusOutlined, SearchOutlined } from '@ant-design/icons'
import { PropTypes } from 'prop-types'
import { css } from 'glamor'

import defaultStyles from './styles'
const { Option } = Select
const { RangePicker } = DatePicker

const ScorchTable = ({
  columns,
  dataSource,
  tableSize,
  styleObj,
  pagination,
  onSortChange,
  filters,
  filtersSize,
  onFilter,
  onResetFilters,
  loading,
  expandableRows,
  onExpandRow,
  onCloseRow,
  expandedRowRender,
  expandedRowIdentifier,
  rowExpandIcon,
  rowCloseIcon,
  expandMulti,
  customExpandedRowKeys,
  customSetExpandedRowKeys,
  showExpandColumn
}) => {
  const [defaultExpandedRowKeys, defaultSetExpandedRowKeys] = useState([])

  const expandedRowKeys = customExpandedRowKeys || defaultExpandedRowKeys
  const setExpandedRowKeys =
    customSetExpandedRowKeys || defaultSetExpandedRowKeys

  const [form] = Form.useForm()

  const handleSortChange = (pagination, filters, sorter) => {
    onSortChange(pagination, sorter)
  }

  const resetFilters = () => {
    form.resetFields()
    if (onResetFilters) {
      onResetFilters()
    }
  }

  const onTableRowExpand = (expanded, record) => {
    const rowIdentifier = expandedRowIdentifier || 'key'

    if (expanded) {
      onExpandRow(record)
    } else {
      onCloseRow(record)
    }

    let keys = []
    // Keep array of open keys empty if expand multi not true
    if (expandMulti) {
      keys = [...expandedRowKeys]
    }

    if (expanded) {
      keys = [record[rowIdentifier], ...keys]
    } else {
      keys = keys.filter((item) => item !== record[rowIdentifier])
    }

    setExpandedRowKeys(keys)
  }

  const getExpandableProps = () => {
    if (expandableRows) {
      const rowIdentifier = expandedRowIdentifier || 'key'
      const expandIcon = rowExpandIcon || <SearchOutlined />
      const closeIcon = rowCloseIcon || <MinusOutlined />
      return {
        rowKey: rowIdentifier,
        expandedRowKeys: expandedRowKeys,
        onExpand: onTableRowExpand,
        showExpandColumn: showExpandColumn,
        expandable: {
          expandIcon: ({ expanded, onExpand, record }) => (
            <div
              onClick={(e) => onExpand(record, e)}
              style={{ cursor: 'pointer' }}
            >
              {expanded ? closeIcon : expandIcon}
            </div>
          ),
          expandedRowRender: expandedRowRender
        }
      }
    }
  }

  const getInputElement = (el) => {
    switch (el.type) {
      case 'date':
        return <DatePicker size={filtersSize} />

      case 'date-range':
        return (
          <RangePicker format={el.format || 'DD-MM-y'} size={filtersSize} />
        )
      case 'select':
        return (
          <Select defaultValue={el.defaultValue || el.label} size={filtersSize}>
            {el.options.map((option) => {
              return (
                <Option key={`option${option.value}`} value={option.value}>
                  {option.label}
                </Option>
              )
            })}
          </Select>
        )
      default:
        return (
          <Input
            size={filtersSize}
            placeholder={el.placeholder}
            type={el.type || 'text'}
          />
        )
    }
  }

  return (
    <div
      className='tableContainer'
      {...css(defaultStyles.tableContainer, styleObj.tableContainer)}
    >
      {filters.length > 0 && (
        <div
          className='filtersContainer'
          {...css(defaultStyles.filtersContainer)}
        >
          <Form form={form} onFinish={onFilter}>
            <Row type='flex' align='bottom' gutter={24}>
              {filters.map((el) => {
                return (
                  <Col key={`filter-${el.name}`}>
                    <Form.Item label={el.label} name={el.name}>
                      {getInputElement(el)}
                    </Form.Item>
                  </Col>
                )
              })}
              <Col>
                <Form.Item label='' name='resetFilter'>
                  <Button
                    size={filtersSize}
                    type='secondary'
                    onClick={resetFilters}
                  >
                    Reset filters
                  </Button>
                </Form.Item>
              </Col>
              <Col>
                <Form.Item label='' name='filter'>
                  <Button size={filtersSize} type='primary' htmlType='submit'>
                    Filter
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={pagination}
        onChange={handleSortChange}
        size={tableSize}
        loading={loading}
        tableLayout='fixed'
        {...getExpandableProps()}
      />
    </div>
  )
}

ScorchTable.propTypes = {
  columns: PropTypes.array.isRequired,
  dataSource: PropTypes.array.isRequired,
  tableSize: PropTypes.string,
  styleObj: PropTypes.object,
  pagination: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  onSortChange: PropTypes.func,
  filters: PropTypes.array,
  filtersSize: PropTypes.string,
  onFilter: PropTypes.func,
  onResetFilters: PropTypes.func,
  loading: PropTypes.bool,
  expandableRows: PropTypes.bool,
  onExpandRow: PropTypes.func,
  onCloseRow: PropTypes.func,
  expandedRowRender: PropTypes.func,
  expandedRowIdentifier: PropTypes.string,
  rowExpandIcon: PropTypes.node,
  rowCloseIcon: PropTypes.node,
  expandMulti: PropTypes.bool
}
ScorchTable.defaultProps = {
  tableSize: 'middle',
  styleObj: {},
  pagination: false,
  onSortChange: () => {},
  filters: [],
  filtersSize: 'medium',
  onFilter: () => {},
  onResetFilters: () => {},
  loading: false,
  expandableRows: false,
  onExpandRow: () => {},
  onCloseRow: () => {},
  expandedRowRender: undefined,
  expandedRowIdentifier: undefined,
  rowExpandIcon: undefined,
  rowCloseIcon: undefined,
  expandMulti: false,
  customExpandedRowKeys: undefined,
  customSetExpandedRowKeys: undefined,
  showExpandColumn: true
}

export default ScorchTable
